import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import CountUp from "react-countup";
import { ShieldCheck, ArrowUpRight, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { CS } from "@/constants/testIds/clickshield";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 + i * 0.12 },
  }),
};

export const Hero = () => {
  const nav = useNavigate();
  const [blocked, setBlocked] = useState(48213765);
  const [offer, setOffer] = useState(null);
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    api.get("/launch-offer").then((r) => setOffer(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!offer) return;
    const tick = () => {
      const ms = new Date(offer.ends_at).getTime() - Date.now();
      setRemaining(ms > 0 ? ms : 0);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [offer]);

  const countdown = (ms) => {
    const d = Math.floor(ms / 86400000);
    const h = String(Math.floor((ms % 86400000) / 3600000)).padStart(2, "0");
    const m = String(Math.floor((ms % 3600000) / 60000)).padStart(2, "0");
    const s = String(Math.floor((ms % 60000) / 1000)).padStart(2, "0");
    return `${d}d ${h}:${m}:${s}`;
  };
  const shellRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  useEffect(() => {
    let mounted = true;
    const fetchStats = () =>
      api.get("/stats/live").then((r) => mounted && setBlocked(r.data.blocked_clicks)).catch(() => {});
    fetchStats();
    const t = setInterval(fetchStats, 4000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const onMove = (e) => {
    const r = shellRef.current?.getBoundingClientRect();
    if (!r) return;
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: px * 18, y: -py * 18 });
  };

  const headline = ["Stop wasting up to 30%", "of your ad budget", "on fake clicks."];

  return (
    <section
      ref={shellRef}
      onMouseMove={onMove}
      className="relative min-h-screen flex items-center overflow-hidden bg-tech-grid pt-24"
    >
      {/* ambient glow */}
      <div className="absolute -top-40 -right-32 w-[38rem] h-[38rem] rounded-full bg-cyan-neon/10 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 -left-40 w-[32rem] h-[32rem] rounded-full bg-[#39FF14]/10 blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center relative z-10 w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="flex flex-wrap items-center gap-3 mb-8"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-neon/30 bg-cyan-neon/5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse-glow" />
              <span data-testid="hero-live-badge" className="font-mono text-[11px] tracking-widest uppercase text-cyan-neon">
                Now live at clickshieldai.com
              </span>
            </div>
            {offer && remaining !== null && remaining > 0 && (
              <a
                href="#pricing"
                data-testid="hero-launch-banner"
                className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#39FF14]/30 bg-[#39FF14]/5 hover:bg-[#39FF14]/10 transition-colors"
              >
                <span className="font-mono text-[11px] tracking-widest uppercase text-[#39FF14]">
                  Launch week · {offer.percent}% off 3 months · code {offer.code}
                </span>
                <span data-testid="hero-launch-countdown" className="font-mono text-[11px] tracking-wider uppercase text-amber-400 tabular-nums">
                  ends in {countdown(remaining)}
                </span>
                <span className="text-[#39FF14] group-hover:translate-x-0.5 transition-transform">→</span>
              </a>
            )}
          </motion.div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-white">
            {headline.map((l, i) => (
              <span key={i} className="block overflow-hidden py-1">
                <motion.span
                  custom={i}
                  variants={line}
                  initial="hidden"
                  animate="show"
                  className="block"
                >
                  {i === 2 ? (
                    <>
                      on <span className="text-cyan-neon text-glow-cyan">fake clicks.</span>
                    </>
                  ) : (
                    l
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="mt-7 text-lg text-slate-300 font-light max-w-xl leading-relaxed"
          >
            ClickShield AI detects bots, click farms and competitor sabotage in real time — then
            auto-blocks malicious IPs across Google, Meta &amp; Bing before they drain your budget.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid={CS.heroTrial}
              onClick={() => nav("/signup")}
              className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-cyan-neon text-[#040914] font-semibold hover:-translate-y-0.5 hover:glow-cyan transition-all"
            >
              Start 14-day free trial
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
            <button
              data-testid={CS.heroDemo}
              onClick={() => document.getElementById("calculator")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/15 text-white font-medium hover:border-cyan-neon/50 hover:bg-white/5 transition-colors"
            >
              <Zap className="w-4 h-4 text-[#39FF14]" /> Calculate my savings
            </button>
          </motion.div>

          <p className="mt-4 font-mono text-xs text-slate-500 tracking-wide">
            No credit card required · Setup in under 60 seconds
          </p>
        </div>

        {/* right: live counter + floating shield */}
        <div className="lg:col-span-5 flex justify-center lg:justify-end">
          <motion.div
            style={{ transform: `perspective(1000px) rotateY(${tilt.x}deg) rotateX(${tilt.y}deg)` }}
            className="relative w-full max-w-sm rounded-2xl neon-border bg-[#0B101A]/80 backdrop-blur-xl p-8 transition-transform duration-200"
          >
            <div className="flex items-center justify-between mb-6">
              <span className="font-mono text-[11px] tracking-widest uppercase text-slate-400">Fraud blocked · all time</span>
              <ShieldCheck className="w-5 h-5 text-[#39FF14]" />
            </div>
            <div data-testid={CS.heroCounter} className="font-display text-4xl sm:text-5xl text-white tracking-tight tabular-nums">
              <CountUp end={blocked} separator="," duration={2.2} preserveValue />
            </div>
            <div className="mt-1 text-sm text-cyan-neon font-medium">malicious clicks stopped</div>

            <div className="mt-8 h-px bg-white/10" />

            <div className="mt-6 grid grid-cols-2 gap-4">
              <div>
                <div className="font-display text-2xl text-white">$12.7M+</div>
                <div className="font-mono text-[10px] tracking-wider uppercase text-slate-500 mt-1">Budget saved</div>
              </div>
              <div>
                <div className="font-display text-2xl text-white">8,420</div>
                <div className="font-mono text-[10px] tracking-wider uppercase text-slate-500 mt-1">Domains protected</div>
              </div>
            </div>

            {/* animated bars */}
            <div className="mt-7 flex items-end gap-1.5 h-16">
              {[40, 65, 30, 80, 55, 92, 48, 70, 35, 60].map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0 }}
                  animate={{ height: `${h}%` }}
                  transition={{ delay: 1.2 + i * 0.05, duration: 0.6 }}
                  className={`flex-1 rounded-sm ${i % 3 === 0 ? "bg-[#39FF14]/70" : "bg-cyan-neon/60"}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
